import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import { Author } from '../Author'
import { QuoteEndIcon } from '../Icons/Navigational/QuoteEnd'
import { QuoteStartIcon } from '../Icons/Navigational/QuoteStart'
import { ImageCredit } from '../Image/ImageCredit'
import { Picture } from '../Picture'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Quote.module.scss'

export const Quote: FunctionComponent<Props> = ({
  authorName,
  image,
  jobTitle,
  quote,
  className,
  ...rest
}) => {
  return (
    <figure
      data-testid="quote-wrapper"
      className={classnames(styles.quote, className)}
      {...rest}
    >
      <span className={styles.quoteText}>
        {image?.src && (
          <div className={styles.quoteImage}>
            <Picture data-testid="quote-image" {...image} />
            {image?.credit && (
              <ImageCredit
                className={styles.quoteImageCredit}
                credit={image.credit}
              />
            )}
          </div>
        )}
        <blockquote>
          <TypographyV2 textStyle="sb-text-heading-standfirst">
            <QuoteStartIcon className={styles.quoteIconStart} />
            <QuoteEndIcon className={styles.quoteIconEnd} />
            {quote}
          </TypographyV2>
        </blockquote>
      </span>
      {authorName && (
        <figcaption>
          <Author
            className={styles.quoteAuthor}
            textOnly
            {...{ name: authorName, jobTitle }}
          />
        </figcaption>
      )}
    </figure>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Name of author quote is attributed to.
   */
  authorName?: string
  /**
   * Optional Image src
   */
  image?: {
    alt?: string
    src: string
    credit?: string
  }
  /**
   * Job title of author of the quote. This will not be displayed unless an Author name is also provided.
   */
  jobTitle?: string
  /**
   * Text of the quote to be displayed.
   */
  quote: string
  /**
   * Optional class name
   */
  className?: string
} & ComponentPropsWithoutRef<'figure'>
